import { FC, useEffect, useState } from "react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Box, Callout, Heading } from "@radix-ui/themes";
import "@github/markdown-toolbar-element";
import NewsForm from "@/features/news/components/manage/NewsForm";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { NewsProps } from "@/features/news/types/NewsTypes";
import { firestore, timestamp } from "@/api/firebase/firebase.config";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import useSubmitNews from "@/features/news/hooks/useSubmitNews";
import { useParams } from "react-router-dom";
import {
  ExclamationTriangleIcon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import i18next from "i18next";

const NewsManage: FC = (): JSX.Element => {
  const currentUser = useCurrentUserProfile((s) => s.user);
  const { slug } = useParams<{ slug: string }>();

  const [values, setValues] = useState<NewsProps>({
    title: "",
    slug: "",
    intro: "",
    content: "",
    imageURL: "",
    client: brand.tenantId,
    created: timestamp.now(),
    createdBy: currentUser.uid,
  });

  const { submitNews, loading, error, success } = useSubmitNews(values);

  useEffect(() => {
    if (slug) {
      firestore
        .collection("news")
        .doc(brand.tenantId)
        .collection("articles")
        .doc(slug)
        .get()
        .then((doc) => {
          if (doc.exists) {
            setValues(doc.data() as NewsProps);
          }
        });
    }
  }, [slug]);

  // Page Document Title
  useDocumentTitle(slug ? "Edit News" : "Add News");

  return (
    <Box position="relative">
      <Heading as="h1">{slug ? "Edit News" : "Add News"}</Heading>

      {error && (
        <Callout.Root color="red" mb="3">
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>
            {i18next.t("feedback.error")} {error.message}
          </Callout.Text>
        </Callout.Root>
      )}

      {success && (
        <Callout.Root mb="3">
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>{i18next.t("feedback.success")}</Callout.Text>
        </Callout.Root>
      )}

      <NewsForm
        values={values}
        setValues={setValues}
        submit={submitNews}
        loading={loading}
        isEdit={!!slug}
      />
    </Box>
  );
};

export default NewsManage;
